import { z } from "zod";

/**
 * API error envelope — PRD §5.0. Every non-2xx response body validates against this.
 * Codes are the closed set defined in PRD §5.0 + Waiting Room §5.0; extended per milestone.
 */
export const ErrorCode = z.enum([
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "HANDLE_UNRESOLVED",
  "DOOR_SUPPRESSED",
  "DOOR_NOT_FOUND",
  "DOOR_CLOSED",
  "DOOR_NOT_OPEN",
  /**
   * A turn is already streaming against this knock (§5.4 rule 4's lock). It no
   * longer means "you already have a knock at this door": under R-0908-4 §2
   * that is not a refusal at all — a sender has ONE conversation at a door and
   * knocking again returns it — so the only thing left that this code says is
   * that two turns raced and one of them must wait.
   */
  "KNOCK_ACTIVE",
  /**
   * The doorman is not listening any more (R-0908-4 §3, §8): the owner opened
   * the door and the write-back lane is the road, or blocked this sender, or
   * the guard ended the hearing. Never said about a knock still at the door,
   * and never about one that has simply had its read published — a read is not
   * an ending.
   */
  "KNOCK_CLOSED",
  // M8: unknown knock id, or one that belongs to someone else. Deliberately the
  // same code for both — "exists, but not yours" is itself a disclosure.
  "KNOCK_NOT_FOUND",
  "DOOR_SPEND_CAP",
  "CEILING_BELOW_FLOOR",
  /**
   * THIS WINDOW'S ALLOWANCE IS SPENT (ToU 2.2 §5.3; rulings R-0916-1,
   * R-0923-2, R-0923-3 (1)).
   *
   * It replaces `INSUFFICIENT_CREDITS` and `WALLET_FROZEN`, and the difference
   * is not a rename: there is no balance to be short of any more. Work pauses
   * until the next allowance window or until the account moves up a tier — and
   * the refusal's `details` carry exactly those roads, so the screen that
   * refused can offer the fix.
   *
   * On Free and during a trial the only road is "start the plan", and the body
   * says so.
   */
  "PLAN_EXHAUSTED",
  /**
   * A DISPUTE IS OPEN ON THIS ACCOUNT (ToU 2.2 §8.9, ruling R-0916-1). Paid
   * features are suspended while it is, which is the payment-risk control that
   * replaced the wallet freeze. Cleared when the dispute closes won; a lost
   * close is a different decision and blocks the identity.
   */
  "PAYMENT_RISK_SUSPENDED",
  /** The client returned a hash of words the server did not author. Fail-closed. */
  "CONSENT_MISMATCH",
  // FR-5.6: the Founder's Grant was refused — already taken, ineligible
  // identity, or the global daily cap is exhausted. One code, no detail about
  // which, so the refusal cannot be used to probe the cap.
  "GRANT_INELIGIBLE",
  "WORK_EXCEEDS_BUDGET",
  "RATE_LIMITED",
  "INPUT_REFUSED",
  "RULES_TOO_LARGE",
  "RULES_VERSION_NOT_FOUND",
  "PROPOSAL_NOT_FOUND",
  "PROPOSAL_RESOLVED",
  "WORK_ITEM_NOT_FOUND",
  "WORK_DECISION_CLOSED",
  "REGULATED_CATEGORY",
  "PROVIDER_UNAVAILABLE",
  "VALIDATION_ERROR",
  "ALREADY_REGISTERED",
  "HANDLE_SUPPRESSED",
  "REGISTRATION_LIMIT",
  "REGISTRATION_REFUSED",
  "UNDO_EXPIRED",
  // docs/channels.md: connecting the email channel needs an address to confirm,
  // and an account that has not finished the onboarding run has none yet.
  //
  // `X_IDENTITY_REQUIRED` used to sit at the top of this list as its mirror and
  // is DELETED (ruling R-0828-3): `users.x_user_id` and `users.x_handle` are
  // NOT NULL, so a signed-in session without an X identity is not a state the
  // product can be in, and a refusal for it was a sentence no one could read.
  "EMAIL_REQUIRED",
  /**
   * THE DAY'S FREE LOOKUPS ARE SPENT AND THIS ACCOUNT HAS NO PLAN (ruling
   * R-0910-2 #4). Asking X who holds a handle costs about a cent, every account
   * gets the same handful of those a day for nothing, and past that a Free
   * account is refused rather than charged — it has no allowance to meter
   * against and no wallet lane behind it.
   *
   * TWO CODES RATHER THAN ONE, and the difference is the sentence each carries:
   * a Free account is offered a plan, a paid one is told to come back tomorrow.
   * A single code would have to guess which, and the guess would be wrong for
   * exactly the person who has already paid us.
   */
  "LOOKUP_LIMIT_FREE",
  /**
   * The day's free lookups are spent AND this account's plan allowance is used
   * up (ruling R-0910-2 #4, as amended by R-0916-1). Nothing left to meter it
   * against, so the call is not made. A paid account meets `PLAN_EXHAUSTED`'s
   * roads through the plan screen; this code is what the lookup surface says.
   */
  "LOOKUP_LIMIT_PAID",
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ErrorEnvelope = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    http_status: z.number().int(),
    retryable: z.boolean(),
    details: z.record(z.string(), z.unknown()).optional(),
    request_id: z.string().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>;
